/**
 * Environment Configuration and Validation
 * 
 * WHY THIS FILE EXISTS:
 * - Centralizes all environment variable management
 * - Validates environment variables at startup to catch configuration errors early
 * - Provides type-safe access to environment variables throughout the app
 * - Prevents runtime errors from missing or invalid environment variables
 * 
 * WHAT IT DOES:
 * - Uses Zod schema validation to ensure all required env vars are present and valid
 * - Provides default values for development environment
 * - Throws descriptive errors if validation fails
 * - Exports a typed `env` object for use throughout the application
 * 
 * WHY ZOD IS USED:
 * - Zod provides runtime type validation (TypeScript only provides compile-time checking)
 * - Validates data types, formats (URLs, emails), and constraints (min length, etc.)
 * - Provides clear error messages when validation fails
 * - Generates TypeScript types automatically from schema
 */

import { z } from 'zod';

/**
 * Environment Variable Schema
 * 
 * WHY EACH FIELD EXISTS:
 * 
 * DATABASE_URL: PostgreSQL connection string required for Prisma ORM
 * - Format: postgresql://user:password@host:port/database?schema=public
 * - Used by Prisma to connect to the database
 * 
 * JWT_SECRET: Secret key for signing access tokens
 * - Must be kept secret and never committed to version control
 * - Used to sign and verify JWT access tokens
 * - Should be at least 32 characters long for security
 * 
 * JWT_REFRESH_SECRET: Separate secret for refresh tokens
 * - Different from access token secret for additional security
 * - If one secret is compromised, the other remains secure
 * - Refresh tokens have longer expiration (7 days vs 15 minutes)
 * 
 * JWT_EXPIRES_IN: Access token expiration time
 * - Short expiration (15m) reduces risk if token is stolen
 * - User must refresh token periodically
 * - Format: "15m", "1h", "7d" (jsonwebtoken library format)
 * 
 * JWT_REFRESH_EXPIRES_IN: Refresh token expiration time
 * - Longer expiration (7d) allows users to stay logged in
 * - Used to generate new access tokens without re-login
 * 
 * NODE_ENV: Environment mode
 * - Controls behavior (development vs production)
 * - Affects error messages, logging, etc.
 * 
 * PORT: Server port number
 * - Default 3000 for Next.js
 * - Can be overridden for deployment
 * 
 * API_BASE_URL: Base URL for API endpoints
 * - Used for generating absolute URLs (webhooks, email links, etc.)
 * - Must be a valid URL format
 * 
 * Payment & Third-party API keys: Optional for development
 * - Only required when using those specific features
 * - Marked optional so app can run without them
 */
const envSchema = z.object({
  // Database
  DATABASE_URL: z.string().min(1, 'DATABASE_URL is required'),

  // JWT
  JWT_SECRET: z.string().min(1, 'JWT_SECRET is required'),
  JWT_REFRESH_SECRET: z.string().min(1, 'JWT_REFRESH_SECRET is required'),
  JWT_EXPIRES_IN: z.string().default('15m'),
  JWT_REFRESH_EXPIRES_IN: z.string().default('7d'),

  // Application
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
  PORT: z.string().default('3000'),
  API_BASE_URL: z.string().url().default('http://localhost:3000'),

  // Payment (optional for now)
  RAZORPAY_KEY_ID: z.string().optional(),
  RAZORPAY_KEY_SECRET: z.string().optional(),
  STRIPE_SECRET_KEY: z.string().optional(),

  // Third-party APIs (optional for now)
  OPENWEATHERMAP_API_KEY: z.string().optional(),
  GOOGLE_MAPS_API_KEY: z.string().optional(),
  GOOGLE_CLIENT_ID: z.string().optional(),
  GOOGLE_CLIENT_SECRET: z.string().optional(),
  MSG91_AUTH_KEY: z.string().optional(),
  
  // SendGrid Email Configuration
  SENDGRID_API_KEY: z.string().optional(),
  SENDGRID_FROM_EMAIL: z.string().email().optional(),
  SENDGRID_ENABLED: z.string().transform((val) => val === 'true').default('false'),
  
  // OTP Configuration
  OTP_EXPIRY_MINUTES: z.string().transform((val) => parseInt(val, 10)).default('10'),
  OTP_MAX_ATTEMPTS: z.string().transform((val) => parseInt(val, 10)).default('3'),
});

type Env = z.infer<typeof envSchema>;

/**
 * Get and validate environment variables
 * 
 * WHY THIS FUNCTION EXISTS:
 * - Validates all environment variables at application startup
 * - Provides development defaults so developers can run the app without .env file
 * - Throws clear errors if validation fails, preventing runtime issues
 * 
 * HOW IT WORKS:
 * 1. Merges process.env with development defaults
 * 2. Validates against Zod schema
 * 3. Returns typed environment object or throws descriptive error
 * 
 * WHY DEFAULTS ARE PROVIDED:
 * - Makes development easier (no need to set up .env immediately)
 * - WARNING: Defaults should NEVER be used in production
 * - Forces developers to set proper values via .env file
 */
function getEnv(): Env {
  // During Next.js build, skip strict validation to prevent build failures
  // Environment variables will be validated at runtime instead
  const isBuildTime = 
    process.env.NEXT_PHASE === 'phase-production-build' || 
    process.env.NEXT_PHASE === 'phase-development-build' ||
    process.env.NEXT_PHASE === 'phase-export' ||
    // Vercel build detection
    (process.env.VERCEL === '1' && typeof process.env.DATABASE_URL === 'undefined') ||
    // General build detection (no DATABASE_URL and not in development)
    (typeof process.env.DATABASE_URL === 'undefined' && process.env.NODE_ENV !== 'development');

  // Provide defaults for development (NEVER use these in production!)
  const envWithDefaults = {
    DATABASE_URL: process.env.DATABASE_URL || 'postgresql://localhost:5432/booknaav',
    JWT_SECRET: process.env.JWT_SECRET || 'dev-secret-key-change-in-production-min-32-chars-long',
    JWT_REFRESH_SECRET: process.env.JWT_REFRESH_SECRET || 'dev-refresh-secret-key-change-in-production-min-32-chars-long',
    ...process.env, // Spread actual env vars to override defaults
  };

  try {
    // Parse and validate against schema
    // This will throw ZodError if validation fails
    return envSchema.parse(envWithDefaults);
  } catch (error) {
    if (error instanceof z.ZodError) {
      // During build time, use defaults instead of throwing errors
      if (isBuildTime) {
        console.warn('⚠️  Environment validation skipped during build. Using defaults.');
        // Return a minimal valid env object for build time
        return {
          DATABASE_URL: envWithDefaults.DATABASE_URL,
          JWT_SECRET: envWithDefaults.JWT_SECRET,
          JWT_REFRESH_SECRET: envWithDefaults.JWT_REFRESH_SECRET,
          JWT_EXPIRES_IN: '15m',
          JWT_REFRESH_EXPIRES_IN: '7d',
          NODE_ENV: 'production' as const,
          PORT: '3000',
          API_BASE_URL: 'http://localhost:3000',
          OTP_EXPIRY_MINUTES: 10,
          OTP_MAX_ATTEMPTS: 3,
          SENDGRID_ENABLED: false,
        } as Env;
      }
      // Format validation errors into readable message
      const missingVars = error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('\n');
      throw new Error(`Invalid environment variables:\n${missingVars}`);
    }
    throw error;
  }
}

export const env = getEnv();
